import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  Button,
  PageHeading,
  Icon,
  ConfirmDialog,
  employeeID,
  companyID,
} from "../../../Common/UIComponents";
import {
  GetCheckListConfig,
  DeleteCheckListConfig,
} from "../../../API_Service/Settings_Slice";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import DashboardLayout from "../../../Layout/DashboardLayout";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id: item.Checklist_Id ?? item.ID ?? `row-${i}`,
    name: item.Checklist ?? item.Name ?? "Unknown",
  }));

const EXPORT_COLUMNS = [{ key: "name", title: "Checklist Name" }];

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function CheckListConfigIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [checkLists, setCheckLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  // ── Fetch ─────────────────────────────────────
  const fetchList = useCallback(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(GetCheckListConfig({ ID: 0, companyID }))
      .then((res) => setCheckLists(toArray(res)))
      .catch((err) =>
        setSettingsError(err?.message ?? "Failed to load checklists"),
      )
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchList();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.editSuccess) {
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.editSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.refresh) {
      fetchList();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => buildTableRows(checkLists), [checkLists]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [searchQuery, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  // Drawer-style create/edit (like DropdownConfigCreate): navigate to the
  // dedicated route, carrying backgroundLocation so it renders on top of
  // this list instead of replacing the page with a blank one.
  const handleEdit = useCallback(
    (row) =>
      navigate("/application-config/checklist/create", {
        state: { mode: "edit", data: row, backgroundLocation: location },
      }),
    [navigate, location],
  );

  const handleAddNew = useCallback(
    () =>
      navigate("/application-config/checklist/create", {
        state: { backgroundLocation: location },
      }),
    [navigate, location],
  );

  const handleDelete = useCallback(
    (row) => setDeleteDialog({ open: true, row }),
    [],
  );
  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false, row: null });
    setDeleteBlocked(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(DeleteCheckListConfig({ id: row?.id, modifiedBy: employeeID }))
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        setDeleteBlocked(false);
        setDeleteDialog({ open: false, row: null });
        setDeleteSuccess(true);
        fetchList();
        setTimeout(() => setDeleteSuccess(false), 3000);
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(
    () => [
      {
        key: "name",
        title: "Checklist Name",
        render: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60] font-bold text-sm">
              {row.name.charAt(0).toUpperCase()}
            </div>
            <span className="font-bold text-[#181c1d]">{row.name}</span>
          </div>
        ),
      },
    ],
    [],
  );

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">
        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <PageHeading
            title="CheckList Management"
            subtitle="Create and manage reusable checklists for use across inspections, audits, and workflows."
          />
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add New Checklist
          </Button>
        </div>

        <ConfigTable
          persistKey="application-config-checklist"
          columns={columns}
          rows={isLoading ? [] : paginatedRows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by Checklist Name..."
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="checklists"
          emptyText={isLoading ? "Loading…" : "No checklists found."}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">
            {settingsError}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={
          deleteBlocked
            ? "This checklist is already mapped to another module. Please remove the mapping before deleting."
            : null
        }
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {deleteSuccess && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold"
          role="status"
        >
          <Icon name="check_circle" className="text-[20px]" filled />
          Checklist Deleted successfully.
        </div>
      )}
      {editSuccess && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold"
          role="status"
        >
          <Icon name="check_circle" className="text-[20px]" filled />
          Checklist Updated successfully.
        </div>
      )}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}
